import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import {
  Wrench, Package, FileText, Users, Bell,
  AlertTriangle, CheckCircle2, Clock, ArrowRight,
  UserCheck, BarChart2, ClipboardCheck,
} from 'lucide-react';

const KPI = ({ icon: Icon, label, value, tone = 'default', onClick, testId }) => {
  const tones = {
    default: 'bg-white border-slate-200',
    blue:    'bg-teal-50 border-teal-200',
    green:   'bg-emerald-50 border-emerald-200',
    amber:   'bg-amber-50 border-amber-200',
    red:     'bg-red-50 border-red-200',
  };
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testId}
      className={`text-left rounded-sm border p-4 transition-shadow ${tones[tone]} ${onClick ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`}>
      <div className="flex items-center gap-2 text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500">
        <Icon size={13} /> {label}
      </div>
      <div className="text-2xl font-display font-extrabold mt-2 text-[#1F7A6E]">{value ?? '—'}</div>
    </button>
  );
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [visitorsToday, setVisitorsToday] = useState(0);
  const [auditDue, setAuditDue] = useState(0);

  useEffect(() => {
    apiGet('/admin/dashboard').then(setD).catch(() => {});
    apiGet('/admin/visitors/today').then(r => setVisitorsToday(r.count || 0)).catch(() => {});
    apiGet('/admin/assets/audit-due').then(r => setAuditDue(r.count || 0)).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Administration</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Admin Dashboard</h1>
        <p className="text-sm text-slate-600 mt-1">Office & Facility Management · Supplies · AMC · Notices</p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <KPI icon={Wrench} label="Open Requests" value={d?.maintenance?.open ?? 0}
          tone={d?.maintenance?.open > 0 ? 'amber' : 'green'}
          onClick={() => nav('/app/admin/maintenance')} testId="kpi-maint-open" />
        <KPI icon={AlertTriangle} label="Urgent" value={d?.maintenance?.urgent ?? 0}
          tone={d?.maintenance?.urgent > 0 ? 'red' : 'default'}
          onClick={() => nav('/app/admin/maintenance')} testId="kpi-maint-urgent" />
        <KPI icon={Package} label="Low Stock Items" value={d?.supplies?.low_stock_count ?? 0}
          tone={d?.supplies?.low_stock_count > 0 ? 'amber' : 'green'}
          onClick={() => nav('/app/admin/supplies')} testId="kpi-supply-low" />
        <KPI icon={FileText} label="AMC Expiring Soon" value={d?.amc?.expiring_soon ?? 0}
          tone={d?.amc?.expiring_soon > 0 ? 'red' : 'green'}
          onClick={() => nav('/app/admin/amc')} testId="kpi-amc-expiring" />
        <KPI icon={Bell} label="Active Notices" value={d?.notices?.active ?? 0}
          tone="blue"
          onClick={() => nav('/app/admin/notices')} testId="kpi-notices-active" />
        <KPI icon={UserCheck} label="Visitors Today" value={visitorsToday}
          tone={visitorsToday > 0 ? 'blue' : 'default'}
          onClick={() => nav('/app/admin/visitors')} testId="kpi-visitors-today" />
        <KPI icon={ClipboardCheck} label="Asset Audit Due" value={auditDue}
          tone={auditDue > 0 ? 'amber' : 'green'}
          onClick={() => nav('/app/admin/assets')} testId="kpi-asset-audit-due" />
      </div>

      {/* Quick nav row */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: 'Visitor Log', path: '/admin/visitors' },
          { label: 'Asset Register', path: '/admin/assets' },
          { label: 'KPI Report', path: '/admin/kpi' },
          { label: 'Room Bookings', path: '#' },
        ].map(({ label, path }) => (
          <button key={label} onClick={() => path !== '#' && nav(path)}
            className="text-xs px-3 py-1.5 border border-slate-200 rounded-sm font-display font-semibold text-[#1F7A6E] hover:bg-slate-50 flex items-center gap-1">
            {label} <ArrowRight size={10} />
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Open maintenance requests */}
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-display font-bold text-sm flex items-center gap-2"><Wrench size={14} /> Open Maintenance</h3>
            <button onClick={() => nav('/app/admin/maintenance')} className="text-xs text-[#1F7A6E] hover:underline flex items-center gap-1">
              View all <ArrowRight size={11} />
            </button>
          </div>
          <div className="divide-y divide-slate-50">
            {d?.maintenance?.recent_open?.length === 0 && (
              <div className="p-4 text-xs text-slate-400 text-center">No open requests</div>
            )}
            {(d?.maintenance?.recent_open || []).map(r => (
              <div key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-display font-semibold truncate">{r.title}</div>
                  <div className="text-[10px] text-slate-500">{r.category}</div>
                </div>
                <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                  r.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                  r.priority === 'high'   ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-600'}`}>{r.priority}</span>
              </div>
            ))}
          </div>
        </div>

        {/* AMC expiring soon */}
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-display font-bold text-sm flex items-center gap-2"><Clock size={14} /> AMC Expiring Soon</h3>
            <button onClick={() => nav('/app/admin/amc')} className="text-xs text-[#1F7A6E] hover:underline flex items-center gap-1">
              View all <ArrowRight size={11} />
            </button>
          </div>
          <div className="divide-y divide-slate-50">
            {d?.amc?.expiring_list?.length === 0 && (
              <div className="p-4 text-xs text-slate-400 text-center">No AMCs expiring soon</div>
            )}
            {(d?.amc?.expiring_list || []).map(a => (
              <div key={a.id} className="px-4 py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-display font-semibold truncate">{a.title}</div>
                  <div className="text-[10px] text-slate-500">{a.vendor_name}</div>
                </div>
                <span className="shrink-0 text-[9px] font-bold text-amber-700">{a.end_date}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Important notices */}
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-display font-bold text-sm flex items-center gap-2"><Bell size={14} /> Important Notices</h3>
            <button onClick={() => nav('/app/admin/notices')} className="text-xs text-[#1F7A6E] hover:underline flex items-center gap-1">
              View all <ArrowRight size={11} />
            </button>
          </div>
          <div className="divide-y divide-slate-50">
            {d?.notices?.important?.length === 0 && (
              <div className="p-4 text-xs text-slate-400 text-center">No important notices</div>
            )}
            {(d?.notices?.important || []).map(n => (
              <div key={n.id} className="px-4 py-2.5">
                <div className="text-xs font-display font-semibold truncate">{n.title}</div>
                <div className="text-[10px] text-slate-500">{n.notice_type}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Low stock supplies */}
      {(d?.supplies?.low_stock_items?.length > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-amber-600" />
            <span className="font-display font-bold text-sm text-amber-800">Low / Out-of-Stock Supplies</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {d.supplies.low_stock_items.map(s => (
              <span key={s.id} className="text-xs bg-white border border-amber-200 px-2 py-1 rounded-sm text-amber-800 font-display font-semibold">
                {s.name} — {s.current_stock} {s.category === 'Out' ? '(OUT)' : 'left'}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
